<template>
  <div class="plyr-player" :class="{ 'is-mirror': isMirror }" @contextmenu.prevent>
    <vue-plyr
      ref="video_block"
      :options="options"
      @play="unlock_params"
      @pause="$emit('paused')"
      @ready="init"
      @ended="ended"
      @statechange="player_state_changed"
      @loadeddata="onPlayerLoadeddata($event)"
      @error="error($event)"
    >
      <video controls crossorigin playsinline preload>
        <source :src="src" type="video/mp4" />
      </video>
    </vue-plyr>

    <div v-if="playerSpeed !== 1.0" class="plyr-player__speed">x {{ playerSpeed }}</div>
  </div>
</template>

<script>
import VuePlyr from 'vue-plyr';
import 'vue-plyr/dist/vue-plyr.css';
import { mapState } from 'vuex';
import { i18nPlyr } from './plyri18n.js';

export default {
  name: 'VcvPlayer',

  components: {
    VuePlyr
  },

  props: {
    src: {
      type: [String],
      default: null,
      required: true
    },

    poster: {
      type: [String],
      default: ''
    },

    muted: {
      type: Boolean,
      default: false
    },

    volume: {
      type: Number,
      default: 1
    },

    rate: {
      type: Number,
      default: 1
    },

    index: {
      type: Number,
      default: 0
    },

    hasVideo: {
      type: Boolean,
      default: true
    },

    isMirror: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      player: null,
      playerSpeed: null
    };
  },

  computed: {
    ...mapState('user', { user: state => state.data }),

    options() {
      return {
        speed: { selected: this.rate, options: [1.0, 1.25, 1.5, 2.0] },
        controls: [
          'play-large',
          'play',
          'progress',
          'current-time',
          'mute',
          'volume',
          'settings',
          'airplay',
          'fullscreen'
        ],

        muted: this.muted,
        hideControls: false,
        i18n: i18nPlyr(
          this.user !== null && this.user.language.code !== undefined
            ? this.user.language.code
            : window.navigator.language !== undefined
            ? window.navigator.language.split('-')[0]
            : 'en'
          // сначала берем язык из настроек аккаунта пользователя, потом язык браузера по умолчанию, в остальных случаях en
        )
      };
    }
  },

  watch: {
    src(val) {
      this.$refs.video_block.player.source = {
        type: 'video',
        sources: [{ src: val, type: 'video/mp4' }],
        poster: this.poster || ''
      };
    }
  },

  created() {
    // Трансфер контролов плеером, методы которыми можно управлять плеером
    // TODO: фигня какая-то ненужная. Надо убрать будет.
    this.$emit('inited', {
      play: this.play,
      pause: this.pause,
      play_pause: this.play_pause,
      index: this.index
    });
  },

  mounted() {
    this.player = this.$refs.video_block.player;

    this.$refs.video_block.player.on('ready', player => this.init(player));
    this.$refs.video_block.player.on('ended', () => this.ended());
  },

  methods: {
    init() {
      this.$emit('ready');

      // сохраняем объект плеера и подписываемся на события, чтобы передать параметры для других запусков
      this.$refs.video_block.player.on('ratechange', () => {
        this.$emit('changed', {
          volume: this.volume,
          muted: this.muted,
          rate: this.$refs.video_block.player.speed
        });
        this.playerSpeed = this.$refs.video_block.player.speed;
      });
      this.$refs.video_block.player.on('volumechange', () => {
        this.$emit('changed', {
          volume: this.$refs.video_block.player.volume,
          muted: this.$refs.video_block.player.muted,
          rate: this.rate
        });
      });

      this.playerSpeed = this.$refs.video_block.player.speed;
    },

    error(ev) {
      this.$emit('error', ev);
    },

    // TODO: скорее всего плеер в откликах не требует этого события
    unlock_params(player) {
      // выставляем сохраненые параметры
      player.volume(this.volume);
      player.muted(this.muted);
      player.playbackRate(this.rate);
      this.$emit('play');
    },

    play(time) {
      if (this.player === null) {
        return null;
      }
      if (time !== undefined) {
        this.player.currentTime(time);
      }
      this.player.play();
    },

    pause() {
      if (this.player === null) {
        return null;
      }
      this.player.pause();
    },

    ended() {
      if (this.$refs.video_block.player.fullscreen.active) {
        this.$refs.video_block.player.fullscreen.exit();
      }
      this.$emit('ended');
    },

    play_pause(time) {
      if (this.player === null) {
        return null;
      }
      // проверяем статус плеера и делаем инверсионное действие
      if (this.player.paused()) {
        this.play(time);
      } else {
        this.pause();
      }
    },

    // TODO: скорее всего плеер в откликах не требует этого события
    onPlayerLoadeddata(player) {
      this.$emit('onPlayerLoadeddata', player);
    },

    // TODO: скорее всего плеер в откликах не требует этого события
    player_state_changed(ev) {
      this.$emit('player_state_changed', ev);
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .plyr-player {
  position: relative;
  display: flex;
  justify-content: center;

  &__speed {
    position: absolute;
    right: 20px;
    top: 20px;
    color: $green;
    background: rgba(255, 255, 255, 0.5);
    padding: 2px 5px;
    border-radius: 4px;
    font-size: 20px;
  }
}

.plyr-player {
  position: relative;
  height: 450px;

  &.is-mirror ::v-deep video {
    transform: scaleX(-1);
  }

  ::v-deep .plyr--video {
    width: auto;
  }

  video {
    max-height: 100%;
  }

  ::v-deep .plyr__video-wrapper {
    display: flex;
    justify-content: center;
  }
}
</style>

<style>
:root {
  --plyr-color-main: #00d26e;
}
</style>
